import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCompany,
  createCompanyAdministrator,
  createCompanyDocument,
  deleteCompany,
  deleteCompanyAdministrator,
  deleteCompanyDocument,
  listCompanies,
  listCompanyAdministrators,
  listCompanyDocuments,
  setDefaultCompany,
  updateCompany,
  updateCompanyAdministrator,
  type Company,
  type CompanyAdministrator,
  type CompanyAdministratorInput,
  type CompanyDocument,
  type CompanyInput,
  type DeleteCompanyResult,
} from '../lib/companiesApi';
import { useCompanyStore } from '../store/useCompanyStore';

// React Query hooks for the account's firms (multi-company).

export const COMPANIES_KEY = ['companies'] as const;

// Editing a firm can change the branding shown in the sidebar (the default
// firm IS the company profile), so both caches are invalidated together.
function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: COMPANIES_KEY });
  void qc.invalidateQueries({ queryKey: ['company-profile'] });
}

export function useCompanies() {
  return useQuery({
    queryKey: COMPANIES_KEY,
    queryFn: listCompanies,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Guard for the global switcher: if the persisted selection points to a firm
 * that no longer exists (deleted, other account), reset to "all firms".
 */
export function useCompanySelectionGuard(): void {
  const { data: companies } = useCompanies();
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);

  useEffect(() => {
    if (!companies || !activeCompanyId) return;
    if (!companies.some((c) => c.id === activeCompanyId)) {
      setActiveCompanyId(null);
    }
  }, [companies, activeCompanyId, setActiveCompanyId]);
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation<Company, Error, CompanyInput>({
    mutationFn: createCompany,
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation<Company, Error, { id: string; input: Partial<CompanyInput> }>({
    mutationFn: ({ id, input }) => updateCompany(id, input),
    onSuccess: () => invalidate(qc),
  });
}

export function useSetDefaultCompany() {
  const qc = useQueryClient();
  return useMutation<Company, Error, string>({
    mutationFn: setDefaultCompany,
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation<DeleteCompanyResult, Error, string>({
    mutationFn: deleteCompany,
    onSuccess: () => invalidate(qc),
  });
}

// --- Administrators / sole associates (CLE-ADMIN) ---------------------------

function administratorsKey(companyId: string) {
  return ['companies', companyId, 'administrators'] as const;
}

export function useCompanyAdministrators(companyId: string | undefined) {
  return useQuery({
    queryKey: administratorsKey(companyId ?? ''),
    queryFn: () => listCompanyAdministrators(companyId as string),
    enabled: Boolean(companyId),
  });
}

export function useCreateCompanyAdministrator(companyId: string) {
  const qc = useQueryClient();
  return useMutation<CompanyAdministrator, Error, CompanyAdministratorInput>({
    mutationFn: (input) => createCompanyAdministrator(companyId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: administratorsKey(companyId) }),
  });
}

export function useUpdateCompanyAdministrator(companyId: string) {
  const qc = useQueryClient();
  return useMutation<
    CompanyAdministrator,
    Error,
    { mandateId: string; input: Partial<CompanyAdministratorInput> }
  >({
    mutationFn: ({ mandateId, input }) => updateCompanyAdministrator(companyId, mandateId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: administratorsKey(companyId) }),
  });
}

export function useDeleteCompanyAdministrator(companyId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (mandateId) => deleteCompanyAdministrator(companyId, mandateId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: administratorsKey(companyId) }),
  });
}

// --- Company documents ---------------------------------------------------------

function documentsKey(companyId: string) {
  return ['companies', companyId, 'documents'] as const;
}

export function useCompanyDocuments(companyId: string | undefined) {
  return useQuery({
    queryKey: documentsKey(companyId ?? ''),
    queryFn: () => listCompanyDocuments(companyId as string),
    enabled: Boolean(companyId),
  });
}

export function useCreateCompanyDocument(companyId: string) {
  const qc = useQueryClient();
  return useMutation<CompanyDocument, Error, Parameters<typeof createCompanyDocument>[1]>({
    mutationFn: (input) => createCompanyDocument(companyId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: documentsKey(companyId) }),
  });
}

export function useDeleteCompanyDocument(companyId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (documentId) => deleteCompanyDocument(companyId, documentId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: documentsKey(companyId) }),
  });
}
